import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { api, queries, unwrap, type AirframeDetail, type JoinPolicy, type Visibility } from "@/api";
import { ErrorNote } from "@/components/ErrorNote";
import { formatDateTime } from "@/lib/format";
import { rowFor } from "@/components/OnThisPc";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";

export const Route = createFileRoute("/airframes/$id/settings")({
  component: Settings,
});

function Settings() {
  const { id } = Route.useParams();
  const airframe = useQuery(queries.airframe(id));
  if (!airframe.data) return null;
  const role = airframe.data.your_role;
  if (role !== "owner" && role !== "captain") {
    return <p className="muted">Only the owner and the captains can change the settings.</p>;
  }
  // Keyed on the airframe, not on `updated_at`: a save bumps that and would
  // remount the form, dropping the "Saved." note and the drafts.
  return <SettingsForm key={id} id={id} />;
}

function SettingsForm({ id }: { id: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const a = useQuery(queries.airframe(id)).data!;
  const isOwner = a.your_role === "owner";
  const [name, setName] = useState(a.name ?? "");
  const [description, setDescription] = useState(a.description ?? "");
  const [photoUrl, setPhotoUrl] = useState(a.photo_url ?? "");
  const [visibility, setVisibility] = useState<Visibility>(a.visibility);
  const [joinPolicy, setJoinPolicy] = useState<JoinPolicy>(a.join_policy);
  const [maxLease, setMaxLease] = useState(String(a.policy.max_lease_hours));
  const [requireParked, setRequireParked] = useState(a.policy.require_parked);
  const [allowVersionChange, setAllowVersionChange] = useState(a.policy.allow_version_change);
  const [showMembers, setShowMembers] = useState(a.policy.show_members);
  const [conditionProfile, setConditionProfile] = useState(a.policy.condition_profile ?? "realistic");
  const [webhook, setWebhook] = useState("");
  const [transferTo, setTransferTo] = useState("");
  // Unlisting (decision 138): a second press confirms; the record stays, the pilots lose the aircraft.
  const [unlistAsked, setUnlistAsked] = useState(false);
  const unlist = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/airframes/{id}/unlist", { params: { path: { id } } })),
    onSuccess: () => {
      setUnlistAsked(false);
      void queryClient.invalidateQueries({ queryKey: ["airframe", id] });
      void queryClient.invalidateQueries({ queryKey: ["airframes"] });
    },
  });
  const relist = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/airframes/{id}/relist", { params: { path: { id } } })),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["airframe", id] });
      void queryClient.invalidateQueries({ queryKey: ["airframes"] });
    },
  });

  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.PATCH("/api/v1/airframes/{id}", {
          params: { path: { id } },
          body: {
            name: name.trim() || null,
            description: description.trim() || null,
            photo_url: photoUrl.trim() || null,
            visibility,
            join_policy: joinPolicy,
            policy: {
              max_lease_hours: Number(maxLease),
              require_parked: requireParked,
              allow_version_change: allowVersionChange,
              show_members: showMembers,
              condition_profile: conditionProfile,
            },
            ...(webhook.trim() ? { discord_webhook_url: webhook.trim() } : {}),
          },
        }),
      ),
    onSuccess: () => {
      setWebhook("");
      queryClient.invalidateQueries({ queryKey: ["airframe", id] });
      queryClient.invalidateQueries({ queryKey: ["airframes"] });
    },
  });
  // `null` removes the webhook; an empty string is refused as a bad value.
  const clearWebhook = useMutation({
    mutationFn: async () =>
      unwrap(await api.PATCH("/api/v1/airframes/{id}", { params: { path: { id } }, body: { discord_webhook_url: null } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["airframe", id] }),
  });
  const testWebhook = useMutation({
    mutationFn: async () => unwrap(await api.POST("/api/v1/airframes/{id}/webhook-test", { params: { path: { id } } })),
  });
  const transfer = useMutation({
    mutationFn: async () =>
      unwrap(await api.POST("/api/v1/airframes/{id}/transfer", { params: { path: { id } }, body: { to: transferTo.trim() } })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["transfers"] });
      navigate({ to: "/transfers" });
    },
  });

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <form
        className="card space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
        data-testid="settings-form"
      >
        <h2 className="text-lg font-semibold">Airframe</h2>
        <label className="label" htmlFor="name">
          Name
        </label>
        <input id="name" className="input w-full" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} data-testid="name-input" />
        <label className="label" htmlFor="description">
          Description
        </label>
        <textarea id="description" className="textarea w-full" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} />
        <label className="label" htmlFor="photo-url">
          Photo URL
        </label>
        <input id="photo-url" className="input w-full" type="url" placeholder="https://..." value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} maxLength={2048} data-testid="photo-input" />
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="visibility">
              Visibility
            </label>
            <select id="visibility" className="input w-full" value={visibility} onChange={(e) => setVisibility(e.target.value as Visibility)} data-testid="visibility">
              <option value="public">public, listed to every pilot</option>
              <option value="unlisted">by link only</option>
              <option value="hidden">hidden, members only</option>
            </select>
          </div>
          <div>
            <label className="label" htmlFor="join-policy">
              Joining
            </label>
            <select id="join-policy" className="input w-full" value={joinPolicy} onChange={(e) => setJoinPolicy(e.target.value as JoinPolicy)} data-testid="join-policy">
              <option value="request">by request, you approve</option>
              <option value="open">open, anyone signed in</option>
              <option value="invite">invite link only</option>
            </select>
          </div>
        </div>
        <h3 className="pt-2 font-semibold">Flying time</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="label" htmlFor="max-lease">
              An aircraft stays with a pilot at most, hours (1 to 168)
            </label>
            <input id="max-lease" className="input w-full tnum" type="number" min={1} max={168} value={maxLease} onChange={(e) => setMaxLease(e.target.value)} data-testid="max-lease" />
          </div>
          <div className="space-y-2 pt-5">
            <label className="flex items-center gap-2 text-sm" title="Always on in v1 (RF-11)">
              <input type="checkbox" checked={requireParked} onChange={(e) => setRequireParked(e.target.checked)} disabled />
              Return only when parked (always on)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={allowVersionChange} onChange={(e) => setAllowVersionChange(e.target.checked)} data-testid="allow-version-change" />
              Pilots may return a state written by another version of the aircraft (off, such a return is refused and the flight is not written)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={showMembers} onChange={(e) => setShowMembers(e.target.checked)} data-testid="show-members" />
              Show the member list to visitors
            </label>
          </div>
        </div>
        <h3 className="pt-2 font-semibold">Condition</h3>
        <div>
          <label className="label" htmlFor="condition-profile">
            How fast the airframe gets dirty
          </label>
          <select id="condition-profile" className="input w-full" value={conditionProfile} onChange={(e) => setConditionProfile(e.target.value)} data-testid="condition-profile">
            <option value="realistic">Realistic</option>
            <option value="accelerated">Accelerated (five times faster)</option>
            <option value="off">Off (nothing changes)</option>
          </select>
        </div>
        <div className="flex items-center gap-3 pt-2">
          <button type="submit" className="btn btn-primary" disabled={save.isPending} data-testid="save">
            Save
          </button>
          {save.isSuccess ? (
            <span className="text-ok text-sm" data-testid="saved">
              Saved.
            </span>
          ) : null}
        </div>
        <ErrorNote error={save.error} />
      </form>
      <div className="space-y-5">
        {isOwner ? (
          <section className="card space-y-3">
            <h2 className="text-lg font-semibold">Discord webhook</h2>
            <p className="muted text-sm">
              {a.discord_webhook ? "A webhook is set. Paste a new URL to replace it." : "Paste a Discord webhook URL to post the bookings and the returns to your channel."}
            </p>
            <input
              className="input w-full"
              placeholder="https://discord.com/api/webhooks/..."
              value={webhook}
              onChange={(e) => setWebhook(e.target.value)}
              aria-label="Discord webhook URL"
            />
            <p className="muted text-xs">The URL is saved with the other settings when you press Save.</p>
            <div className="flex gap-2">
              <button type="button" className="btn" onClick={() => testWebhook.mutate()} disabled={!a.discord_webhook || testWebhook.isPending}>
                Send a test
              </button>
              <button type="button" className="btn" onClick={() => clearWebhook.mutate()} disabled={!a.discord_webhook || clearWebhook.isPending}>
                Remove
              </button>
            </div>
            {testWebhook.isSuccess ? <p className="text-ok text-sm">Test sent.</p> : null}
            <ErrorNote error={testWebhook.error ?? clearWebhook.error} />
          </section>
        ) : null}
        {isOwner ? <LiverySection id={id} airframe={a} /> : null}
        {isOwner ? (
          <section className="card space-y-3" data-testid="unlist-section">
            <h2 className="text-lg font-semibold">{a.unlisted_at ? "Unlisted" : "Unlist airframe"}</h2>
            {a.unlisted_at ? (
              <>
                <p className="muted text-sm" data-testid="unlisted-since">
                  Off ShariFly since {formatDateTime(a.unlisted_at)}. Only you see it; nobody books it. Its flights and logbook are kept.
                </p>
                <button type="button" className="btn btn-primary" onClick={() => relist.mutate()} disabled={relist.isPending} data-testid="relist">
                  Put it back on ShariFly
                </button>
                <ErrorNote error={relist.error} />
              </>
            ) : (
              <>
                <p className="muted text-sm">Takes the aircraft off ShariFly for every pilot while its record stays with you.</p>
                {unlistAsked ? (
                  <div className="flex flex-wrap items-center gap-2" data-testid="unlist-confirm">
                    <span className="text-sm">The pilots lose the aircraft and are told. Sure?</span>
                    <button type="button" className="btn btn-danger btn-sm" onClick={() => unlist.mutate()} disabled={unlist.isPending} data-testid="unlist-yes">
                      Yes, unlist it
                    </button>
                    <button type="button" className="btn btn-sm" onClick={() => setUnlistAsked(false)} disabled={unlist.isPending}>
                      Keep it
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="btn btn-danger"
                    onClick={() => setUnlistAsked(true)}
                    title="Takes the aircraft off ShariFly for everyone: it leaves every pilot's hangar and the marketplace, nobody can book it, the slots to come lapse, and the pilots are told. Its flights and logbook stay with you, and you can put it back any time from here. Not while it is booked."
                    data-testid="unlist"
                  >
                    Unlist airframe
                  </button>
                )}
                <ErrorNote error={unlist.error} />
              </>
            )}
          </section>
        ) : null}
        {isOwner ? (
          <section className="card space-y-3">
            <h2 className="text-lg font-semibold">Hand over the ownership</h2>
            <p className="muted text-sm">The new owner must be a member and take over within 7 days. You stay a captain.</p>
            <form
              className="flex gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                transfer.mutate();
              }}
            >
              <input className="input flex-1" placeholder="handle of the new owner" value={transferTo} onChange={(e) => setTransferTo(e.target.value)} data-testid="transfer-to" />
              <button type="submit" className="btn btn-danger" disabled={!transferTo.trim() || transfer.isPending} data-testid="transfer-submit">
                Propose
              </button>
            </form>
            <ErrorNote error={transfer.error} />
          </section>
        ) : null}
      </div>
    </div>
  );
}

/**
 * The livery offered to the pilots (F487, decision 102): the owner offers
 * the airframe's livery from the PC that holds its files, with the word
 * that it may be shared (F407), and withdraws it from anywhere; each pilot
 * takes the offered liveries or not on their own PC's Settings.
 */
function LiverySection({ id, airframe }: { id: string; airframe: AirframeDetail }) {
  const host = useHost();
  const queryClient = useQueryClient();
  const status = useQuery({ ...hostQueries.status(host!), enabled: !!host });
  const info = useQuery({ ...hostQueries.info(host!), enabled: !!host });
  const row = rowFor(status.data, id);
  const [wordGiven, setWordGiven] = useState(false);
  const offered = airframe.livery ?? null;
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ["airframe", id] });
    queryClient.invalidateQueries({ queryKey: ["host"] });
  };
  const offer = useMutation({
    mutationFn: () => host!.shareLivery(row!.uuid, true),
    onSuccess: () => {
      setWordGiven(false);
      invalidate();
    },
  });
  const withdraw = useMutation({
    mutationFn: async () => unwrap(await api.DELETE("/api/v1/airframes/{id}/livery", { params: { path: { id } } })),
    onSuccess: invalidate,
  });
  const canOffer = !!host && !!row && !!info.data?.aircraft_dir;
  return (
    <section className="card space-y-3" data-testid="livery-section">
      <h2 className="text-lg font-semibold">Livery</h2>
      <p className="muted text-sm" data-testid="livery-offered">
        {offered
          ? `Livery "${offered.name}" is offered to the pilots (${offered.files} files, ${(offered.bytes / 1_048_576).toFixed(1)} MB${offered.uploaded_by ? `, by ${offered.uploaded_by.name}` : ""}). Each pilot takes the liveries owners offer, or not, on their own PC's Settings.`
          : "No livery offered: the pilots fly the airframe with the livery their own X-Plane has."}
      </p>
      {canOffer ? (
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            offer.mutate();
          }}
        >
          <label className="flex items-center gap-1 text-xs">
            <input type="checkbox" checked={wordGiven} onChange={(e) => setWordGiven(e.target.checked)} aria-label={`I may share the livery of ${row.reg}`} />I may share this livery with the pilots (it is mine, or its licence allows it)
          </label>
          <button type="submit" className="btn btn-sm btn-primary" disabled={!wordGiven || offer.isPending} aria-label={`Offer the livery of ${row.reg}`} data-testid="offer-livery">
            {offered ? `Offer the livery "${row.livery}" again` : `Offer the livery "${row.livery}"`}
          </button>
        </form>
      ) : (
        <p className="muted text-xs">Offer it from the companion on the PC that holds the airframe's files{host ? " (the CL650's folder is not known here yet; see the set-up)" : ""}.</p>
      )}
      {offered ? (
        <button type="button" className="btn btn-sm" onClick={() => withdraw.mutate()} disabled={withdraw.isPending} data-testid="withdraw-livery">
          Withdraw the livery
        </button>
      ) : null}
      {offer.isSuccess ? <p className="text-ok text-sm">Offered: the pilots who take the liveries install it at their next booking.</p> : null}
      <ErrorNote error={offer.error ?? withdraw.error} />
    </section>
  );
}
